/**
 * stageClarity.tsx — the CLOSURE OUTCOME and the ACTION-NEEDED ask, on both the
 * board card and the deal drawer (2026-08-27, stage clarity §1/§2).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY ONE MODULE for four renderings: the card and the drawer show the SAME two
 * server facts at two densities, and the failure mode of splitting them is that
 * the Lost column and the drawer end up describing one closure two ways. The
 * gates and the wording live here once.
 *
 * WHAT THIS FIXES. `clinicalStatusFor` (statusChips.tsx) used to derive its chip
 * from `eligibility`, and BOTH closures land on `not_eligible` — so every closed
 * deal wore "Clinician rejected", including the ones the ENGINE stopped on the
 * patient's own answers with no clinician anywhere near them. The reason WAS
 * recorded (on the deal row and on the closing audit row, with the actor), but
 * no read exposed it, so the drawer could not answer "rejected why, by whom?".
 *
 * SINCE 2026-08-27 the chip is fixed at source too: it derives "rejected" only
 * from the order-sync rejection lane's server-written sentinel and derives
 * NOTHING for a hard stop, so this module is no longer the only thing standing
 * between a machine's decision and a clinician's name. The two surfaces are now
 * complementary rather than one covering for the other — this one states the
 * reason and names the author, the chip states the decision.
 *
 * NO CLOSURE-KIND LABEL, DELIBERATELY. The wire carries no discriminator, and
 * that is the right design: the two closures differ by their ACTOR — a hard stop
 * is SYSTEM (or has no recorded actor at all), a rejection carries the
 * clinician's verified principal. So this module never names a kind. It states
 * the reason and names the author, which is what makes both cases honest without
 * a vocabulary that could be mapped wrongly.
 *
 * R41 — THE SERVER IS THE CONTROL. Nothing here derives a closure or an ask;
 * absence renders NOTHING rather than a guess.
 *
 * R19 — `closedReason` and `actionNeeded.note` are staff/clinical FREE TEXT.
 * They ride the deal row and the audit row only, never a log line or an
 * analytics event. Both render as PLAIN TEXT NODES here (React escapes them) —
 * neither is note HTML, so neither goes near dangerouslySetInnerHTML.
 *
 * WCAG 2.1 AA: every surface is icon + WORDS, never colour alone — the outcome
 * block and the banner are `Alert`s (which carry their own icon), and the card
 * chip pairs the kit's WarningIcon with the visible words "Action needed".
 */
import type { CSSProperties, ReactNode } from 'react';
import type { DealActionNeeded, DealActivityActorRole, DealCard, DealOutcome, DealStage } from '../api/types';
import { formatUtcDateTime } from '../lib/format';
import { Alert, Badge, WarningIcon } from '../ui/kit';
import { STAGE_LABEL } from './stages';
import { chipStyle } from './statusChips';

/**
 * The actor-role chip wording (agreed clinic seam, 2026-08-27).
 *
 * MOVED HERE from DealDrawer.tsx so the activity feed, the outcome block and
 * the action banner all name a role identically — three copies of this map is
 * three chances for "System" to become "Service" on one surface only.
 *
 * A chip renders ONLY for the four enum values — `SYSTEM` is a VERIFIED
 * non-staff principal (guest/scheduler/service, or the internal order-sync
 * caller: the row DID record who acted, just not a person we can name) and
 * reads "System". `null` and an ABSENT field both mean the role simply was not
 * recorded (note ledgers, the ambiguous journey milestones, a boot-backfill
 * close, an older clinic-service) and render NO chip at all — quietly omitting
 * is honest; guessing a role for an unattributed row would not be. A token
 * outside the four (a role added server-first) also renders no chip: the seam
 * pins the vocabulary, and a raw enum token is not staff-facing copy.
 */
export const ACTOR_ROLE_LABEL: Record<string, string> = {
  CLINICIAN: 'Clinician',
  ADMIN: 'Admin',
  PATIENT: 'Patient',
  SYSTEM: 'System',
};

/**
 * The outcome to render for a deal, or null for none.
 *
 * TWO GATES, both about not over-claiming:
 *  - `closed_lost` ONLY. `closed_won` is terminal too, so the server's outcome
 *    block is populated there as well — and painting a won deal with a red
 *    "why this was lost" panel would be plainly wrong. A RECOVERED lead is
 *    handled by the server (its newest stage change is non-terminal, so the
 *    block comes back empty), and this gate backs that up.
 *  - SOMETHING TO SAY. A reason or a closing timestamp; with neither, there is
 *    no outcome to state and the existing chip stands. A closure with an actor
 *    but no reason still renders — "Closed by X on Y" is more honest than the
 *    chip that would otherwise mis-attribute it.
 */
export function dealOutcome(card: DealCard, outcome: DealOutcome | undefined): DealOutcome | null {
  if (card.stage !== 'closed_lost' || !outcome) return null;
  return outcome.closedReason !== null || outcome.closedAt !== null ? outcome : null;
}

/**
 * The outstanding ask to render, or null for none.
 *
 * `required === true` ONLY — an absent block (older clinic-service) and an
 * explicit `required: false` both mean "nothing outstanding". Note that a live
 * ask with a NULL note still returns: the banner's job is to say admin action
 * is due, and it can say that honestly without a quote (see DealActionBanner).
 */
export function dealActionNeeded(actionNeeded: DealActionNeeded | undefined): DealActionNeeded | null {
  return actionNeeded?.required === true ? actionNeeded : null;
}

/** The board card's compact halves — a plain string and a plain boolean, both
 *  optional so an older clinic-service simply renders nothing. */
export function cardClosedReason(card: DealCard): string | null {
  // An empty/whitespace string is "no reason recorded", not a blank line.
  const reason = card.closedReason?.trim();
  return card.stage === 'closed_lost' && reason ? reason : null;
}
export function cardActionNeeded(card: DealCard): boolean {
  return card.actionNeeded === true;
}

/**
 * The resolved actor as an attribution surface needs it.
 *
 * STRUCTURALLY COMPATIBLE WITH `ResolvedActor` in DealDrawer, which is what the
 * drawer passes straight in. It is declared HERE rather than imported from
 * there because DealDrawer imports this module — the other direction would be a
 * cycle — and it deliberately omits `email`: this surface never reveals one, so
 * asking for it would be a field the caller has to supply and nothing reads.
 */
export interface AttributionActor {
  /** `fullName` when identity sent one, otherwise the email standing in for it. */
  name: string;
  /**
   * ⚠️ FALSE MEANS "the name is an email", NOT "no actor". An email fallback is
   * the CORRECT rendering for an account with no `fullName` — see the block in
   * Attribution — and it must keep its role chip, because an email beside no
   * chip says less about who acted than the chip did on its own.
   */
  hasRealName: boolean;
}

/**
 * The roles whose chip may be given up to a name — the same set, and the same
 * reasoning, as `STAFF_ACTOR_ROLES` in DealDrawer.
 *
 * ⚠️ SYSTEM AND PATIENT ARE DELIBERATELY ABSENT. Neither carries a name or an
 * email on this wire, so for those the role chip is the ONLY thing identifying
 * the actor and dropping it would leave the attribution line saying nothing at
 * all about who acted.
 */
const STAFF_ATTRIBUTION_ROLES: ReadonlySet<DealActivityActorRole> = new Set(['CLINICIAN', 'ADMIN']);

/**
 * "<verb> by <role chip> <name> on <datetime>" — the attribution tail shared by
 * the outcome block and the action banner.
 *
 * The SAME actor grammar the audit tracer's ActivityRow uses, and for the same
 * reason: names never travel on the wire (R19), so a CLINICIAN reader gets the
 * coarse ROLE CHIP and nothing else, while an ADMIN additionally gets the
 * display name their one batched staff lookup resolved.
 *
 * EVERY PART IS OPTIONAL because the server sends whichever halves it has. With
 * no actor the "by" clause is DROPPED rather than filled with "unknown" — an
 * engine-fired hard stop and a boot-backfill close genuinely have none. With no
 * timestamp the "on" clause is dropped the same way. If neither survives, the
 * whole line is omitted: an empty "Closed" tells the reader nothing the block's
 * heading has not already said.
 */
function Attribution({
  verb,
  role,
  actor,
  at,
}: {
  /** "Closed" / "Asked" — the leading word, so both surfaces read as sentences. */
  verb: string;
  role: DealActivityActorRole | null;
  /** Resolved by the drawer's ADMIN-only batched lookup; absent for clinicians. */
  actor?: AttributionActor;
  at: string | null;
}): ReactNode {
  const roleLabel = role ? (ACTOR_ROLE_LABEL[role] ?? null) : null;
  const actorName = actor?.name;
  /* ── ONE ACTOR GRAMMAR ACROSS THE DRAWER (2026-09-06) ─────────────────────
   *
   * The audit feed's `ActivityRow` gives the role tag's slot up to a REAL NAME
   * and only to one — "Dr Riyaz Patel", not "CLINICIAN Dr Riyaz Patel". This
   * banner had no `hasRealName` concept at all and printed the chip
   * unconditionally, so the two surfaces were already two grammars and would
   * have diverged VISIBLY the moment identity started sending `fullName`: the
   * feed would read "Dr Riyaz Patel" while the banner beside it read
   * "Asked by CLINICIAN Dr Riyaz Patel".
   *
   * ALL THREE CONJUNCTS ARE THE SAME ONES ActivityRow uses, for the same
   * reasons — a STAFF role (SYSTEM and PATIENT keep the only actor indicator
   * they have), a RESOLVED actor (a clinician viewer resolves nobody and must
   * keep the chip rather than lose the actor entirely), and a REAL NAME rather
   * than an email standing in for one.
   *
   * ✅ THE NAME ARRIVED ON 2026-09-06 (identity #88 + contracts v0.11.0), and
   * this block is kept rather than deleted because it is the reason the change
   * needed no portal edit when it did. It used to say "this changes nothing on
   * today's wire": `staffUserLookupResponse` was `.strict()` over
   * `{ userId, email }`, so `hasRealName` was false on every staff row and every
   * banner kept its chip and its email. Landing the portal half FIRST, gated on
   * a real name rather than on "an actor resolved", is what let both surfaces
   * change together the moment identity added its two lines — instead of the
   * feed gaining names while this banner still printed a role chip in front of
   * them. Accounts with no stored name (the seeded master admin) still take the
   * chip-and-email path, which is the honest rendering for them.
   */
  const namedStaff =
    actor?.hasRealName === true && role != null && STAFF_ATTRIBUTION_ROLES.has(role);
  const showRoleChip = roleLabel !== null && !namedStaff;
  const hasActor = roleLabel !== null || Boolean(actorName);
  const stamp = at ? formatUtcDateTime(at) : '';
  if (!hasActor && !stamp) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontSize: '12.5px' }}>
      <span>{hasActor ? `${verb} by` : verb}</span>
      {showRoleChip ? <Badge tone="muted">{roleLabel}</Badge> : null}
      {actorName ? <span>{actorName}</span> : null}
      {stamp ? <span>on {stamp}</span> : null}
    </span>
  );
}

/** Shared block layout inside an Alert — Alert wraps children in a span, so
 * each line declares `display:block` rather than relying on flow. */
const LINE: CSSProperties = { display: 'block' };
const LEAD: CSSProperties = { ...LINE, fontWeight: 700 };
/** The verbatim quote line — `preWrap` keeps the author's own line breaks, and
 * `anywhere` stops one long unbroken string from widening the drawer. */
const QUOTE: CSSProperties = {
  ...LINE,
  marginTop: '4px',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};
/** A stated absence — italic and quieter than a real quote, so it can never be
 *  mistaken for something a person actually wrote. */
const ABSENT: CSSProperties = { ...LINE, marginTop: '4px', fontStyle: 'italic' };

/**
 * THE OUTCOME BLOCK (drawer Overview) — replaces the bare status chip on a
 * closed deal with the two things staff actually need: the reason VERBATIM, and
 * who closed it when.
 *
 * No `role="status"`: this is static panel content present the moment the
 * Overview renders, so a live region would announce nothing and only add a third
 * competing announcement to a drawer that already has two. Screen readers reach
 * it in reading order, at the top of the panel, where it belongs.
 */
export function DealOutcomeBlock({ outcome, actor }: { outcome: DealOutcome; actor?: AttributionActor }) {
  const reason = outcome.closedReason?.trim();
  return (
    <Alert tone="danger" style={{ marginBottom: 'var(--gps-s-4)' }}>
      <span style={LEAD}>Why this deal was lost</span>
      {reason ? (
        // VERBATIM, as a text node — see the R19 note in the module header.
        <span style={QUOTE}>{reason}</span>
      ) : (
        // The closing lane recorded no reason. Saying so is the honest option:
        // silence here would read as "there was nothing to say".
        <span style={ABSENT}>No reason was recorded when this deal was closed.</span>
      )}
      <span style={{ ...LINE, marginTop: '6px' }}>
        <Attribution verb="Closed" role={outcome.closedByRole} actor={actor} at={outcome.closedAt} />
      </span>
    </Alert>
  );
}

/**
 * THE ACTION BANNER (top of the drawer, ABOVE the tabs) — the ask an admin has
 * been handed, stated verbatim where it cannot be missed.
 *
 * WHY ABOVE THE TABS and not inside a panel: the whole point is that an admin
 * opening the card sees what to do WITHOUT reading note history, and any tab
 * placement makes it something you have to already know to go and find.
 *
 * SHOWN TO BOTH ROLES. The ask is an ADMIN's to action, but hiding it from the
 * clinician would hide the clinician's OWN outstanding request from them — the
 * one person who needs to know it is still sitting there unactioned. The server
 * decides whether an ask is live (R41); the portal does not re-scope it by
 * reader.
 *
 * A NULL NOTE STILL RENDERS. A deal that reached admin's court automatically
 * (a callback escalation) has no ask written down. The banner says that plainly
 * rather than borrowing an older round's note or hiding the fact that admin
 * action is due — an unexplained badge beats a wrong explanation or a silence.
 *
 * `role="status"` per spec — polite, never `alert`: this is a standing state of
 * the deal, not an interruption, and an assertive region would talk over a
 * screen-reader user mid-navigation every time a drawer opened.
 */
export function DealActionBanner({
  actionNeeded,
  actor,
}: {
  actionNeeded: DealActionNeeded;
  actor?: AttributionActor;
}) {
  const note = actionNeeded.note?.trim();
  return (
    <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <span style={LEAD}>Action needed</span>
      {note ? (
        // VERBATIM, as a text node — see the R19 note in the module header.
        <span style={QUOTE}>{note}</span>
      ) : (
        <span style={ABSENT}>
          This deal is waiting on an admin. No note was recorded with the handover — check the Activity tab.
        </span>
      )}
      <span style={{ ...LINE, marginTop: '6px' }}>
        <Attribution verb="Asked" role={actionNeeded.askedByRole} actor={actor} at={actionNeeded.askedAt} />
      </span>
    </Alert>
  );
}

/* ── The not-eligible banner (2026-09-06) ───────────────────────────────────
 *
 * ⚠️ THIS BANNER USED TO ASSERT A STAGE IT NEVER READ. Three copies of an Alert
 * in DealDrawer said "This person is not eligible — the deal has moved to Lost"
 * behind a condition that looked ONLY at the eligibility verdict:
 *
 *     const notEligible =
 *       detail?.assessment?.eligibility === 'not_eligible' || deal.eligibility === 'not_eligible';
 *
 * So a not-eligible deal sitting in an ADMIN lane showed a `NOT ELIGIBLE` chip
 * on a card in "Admin follow-up" and, on opening it, a banner insisting the
 * deal was Lost. Two surfaces contradicting each other about whether there is
 * anything left to do.
 *
 * ⚠️ AND THE CARD WAS THE ONE TELLING THE TRUTH. Both the lane and the chip
 * come from the SAME server payload in `toCards` (`stage: deal.stage` and
 * `eligibility: assessment?.eligibility`) — one fetch, no cache between them,
 * so a "stale lane" is not structurally possible here. The deal really is open;
 * the sentence was wrong.
 *
 * A NOT-ELIGIBLE DEAL IN AN OPEN LANE IS A LEGITIMATE, DESIGNED STATE, which is
 * exactly why this is fixed by making the banner honest rather than by chasing
 * the server. clinic-service's `syncDealOnAssessmentNotEligible` auto-closes to
 * `closed_lost` only for an `isAutoMovable` stage, and it deliberately does NOT
 * fire when:
 *   - the deal is in `pending_admin_review` — `AUTOMATION_HELD_STAGES`, with the
 *     reason written on the hook: "a deal parked for admin stays parked (an
 *     auto-Lost would delete the very case the admin was asked to look at)";
 *   - the deal reached the admin lane AFTER the verdict was recorded — a human
 *     `recover()` of a PAID not-eligible deal lands in `awaiting_admin_followup`
 *     by design, and a duplicate-orphan `releaseStage()` drops a paid orphan
 *     there too. Nothing re-evaluates eligibility on a later stage move.
 *
 * In both cases a human decided this patient is worth a phone call. Making the
 * UI insist they are Lost is what would cut that conversation off.
 *
 * SO THE RULE IS: STATE THE VERDICT, THEN STATE THE STAGE THE SERVER ACTUALLY
 * REPORTS. Never infer one from the other in either direction.
 */

/**
 * The eligibility verdict and the deal's REAL position, in one sentence.
 *
 * `context` is the per-tab tail (what to do on THIS panel) — the three drawer
 * tabs each end the sentence differently and that difference is real, but the
 * verdict-and-stage half in front of it must be identical on all three or the
 * drawer contradicts itself between its own tabs.
 */
export function NotEligibleBanner({ stage, context }: { stage: DealStage; context: ReactNode }) {
  const closed = stage === 'closed_lost';
  return (
    <Alert tone="danger" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
      {closed ? (
        'This person is not eligible — the deal has moved to Lost. '
      ) : (
        <>
          {'This person is not eligible, but this deal is still OPEN in '}
          <strong>{STAGE_LABEL[stage] ?? stage}</strong>
          {/* WHY THE LANE IS NAMED rather than a bare "still open": the two
              routes into this state want different handling — a deal parked in
              "Pending admin review" is waiting for a decision nobody has made
              yet, while one recovered into "Admin follow-up" is a call someone
              has already decided to make. The lane is the only thing on this
              banner that distinguishes them. */}
          {' — it has not been closed. '}
        </>
      )}
      {context}
    </Alert>
  );
}

/**
 * THE CARD'S ACTION-NEEDED CHIP — a small warning pill so a board scan finds
 * the deals with something outstanding without opening any of them.
 *
 * Icon AND words (WCAG 1.4.1): the kit's WarningIcon is decorative
 * (`aria-hidden`), and the visible text "Action needed" is the accessible name,
 * so the chip says the same thing to a sighted scan and a screen reader. The
 * `title` carries the longer phrase for hover.
 */
export function ActionNeededChip() {
  return (
    <span
      title="Action needed — this deal is waiting on an admin"
      style={{ ...chipStyle({ bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' }), gap: '5px' }}
    >
      <WarningIcon size={11} />
      Action needed
    </span>
  );
}

/**
 * THE CARD'S CLOSURE LINE — why this card is in the Lost column, readable
 * without opening it.
 *
 * CLAMPED TO TWO LINES rather than shortened on the wire: ONE reason string
 * exists (the server derives it once and serves it to both reads), the drawer
 * quotes all of it, and the card shows as much as fits with the whole thing on
 * `title`. A separate server-side "short reason" would be a second copy of the
 * same free text, free to drift from the one staff are quoted in the drawer.
 */
export function CardClosedReason({ reason }: { reason: string }) {
  return (
    <span
      title={reason}
      style={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontSize: '12px',
        lineHeight: 1.35,
        color: 'var(--gps-danger-text)',
        overflowWrap: 'anywhere',
      }}
    >
      {reason}
    </span>
  );
}

/* ── Per-owner call attempts (2026-09-05) ───────────────────────────────────
 *
 * "2/3 clinician" and "1/3 admin" as two chips, never one stacked number.
 *
 * WHY THE STACKED NUMBER WAS NOT ENOUGH. "3 attempts" on a card cannot tell an
 * admin the one thing they need on sight: whether the clinician has finished
 * ringing, or whether the deal has only just landed on their own desk. Those
 * two states demand opposite actions and the single count renders them
 * identically.
 *
 * ⚠️ READ, NEVER DERIVED — see the `clinicianAttempts` block in api/types.ts.
 * The portal holds `attemptsThisRound` and `attemptsLifetime` and could infer a
 * split from them; it must not. The epoch rules are subtle enough (handovers
 * reset the round, `escalatedAt` moves in both directions, two ledgers feed the
 * tally) that this screen and the drawer would eventually disagree about how
 * hard we tried to reach a patient.
 */

/** Which side of the handover an attempt count belongs to. */
export type AttemptOwner = 'clinician' | 'admin';

export interface AttemptSplitChip {
  owner: AttemptOwner;
  count: number;
  /** True once the count is PAST `attemptsRequired` — renders "5 attempts", not "5/3". */
  exceeded: boolean;
  /** True at or past the requirement — the threshold that fires escalation/parking. */
  atThreshold: boolean;
  /** The badge face. */
  label: string;
  /** The full sentence for title + aria-label. */
  full: string;
}

const OWNER_LABEL: Record<AttemptOwner, string> = { clinician: 'Clinician', admin: 'Admin' };

/**
 * The per-owner attempt chips for a card, in handover order (clinician, then
 * admin), or `[]` when this board has nothing honest to say.
 *
 * FOUR REASONS TO RENDER NOTHING, all of them "the server did not tell us"
 * rather than "the answer is zero":
 *  - either count is missing → the server predates the split. An older
 *    clinic-service omits the keys entirely (the estate's additive
 *    [CONTRACTS GAP] posture), and painting "0/3 clinician" across a live
 *    board would assert that nobody has phoned anybody.
 *  - `attemptsRequired` is missing → there is no denominator, and this screen
 *    is deliberately not allowed to hardcode the 3 (that is the whole reason
 *    the requirement travels on the wire).
 *  - an owner's count is 0 → that side has not phoned yet. A zero chip is
 *    noise on every fresh card in the lane, and its absence already reads as
 *    "not started" without occupying the row.
 *  - both counts are 0 → the card carries no attempt chrome at all, exactly as
 *    it did before this feature.
 */
export function attemptSplitChips(card: DealCard): AttemptSplitChip[] {
  const required = card.attemptsRequired;
  if (typeof card.clinicianAttempts !== 'number' || typeof card.adminAttempts !== 'number') return [];
  if (typeof required !== 'number' || required <= 0) return [];

  const owners: Array<{ owner: AttemptOwner; count: number; exceeded: boolean }> = [
    { owner: 'clinician', count: card.clinicianAttempts, exceeded: card.clinicianAttemptsExceeded === true },
    { owner: 'admin', count: card.adminAttempts, exceeded: card.adminAttemptsExceeded === true },
  ];

  return owners
    .filter(({ count }) => count > 0)
    .map(({ owner, count, exceeded }) => ({
      owner,
      count,
      exceeded,
      atThreshold: count >= required,
      // PAST THE REQUIREMENT READS AS EFFORT, NOT AS A BROKEN GAUGE. "5/3" is a
      // progress bar that has overflowed its own track — the eye reads it as a
      // rendering fault and the extra two calls, which are real work someone
      // did, are what gets discounted. "5 attempts" states them.
      label: exceeded ? `${count} attempts · ${OWNER_LABEL[owner]}` : `${count}/${required} ${OWNER_LABEL[owner]}`,
      full: exceeded
        ? `${OWNER_LABEL[owner]}: ${count} call attempts made — past the ${required} required`
        : `${OWNER_LABEL[owner]}: ${count} of ${required} call attempts made`,
    }));
}

/**
 * ⚠️ THE PER-OWNER COUNTERS ARE NO LONGER ON THE BOARD CARD (CTO, 2026-09-06),
 * AND THE THRESHOLD WARNING DID NOT GO WITH THEM THIS TIME. Read this before
 * putting a count back on a card.
 *
 * WHY THEY WENT. The board is a SCAN surface and two always-on pills made every
 * card taller: measured at the real 250px chip-row width, a card carrying
 * "3/3 Clinician" + "2/3 Admin" grows 97px → 118px (+22%), and on a busy admin
 * card with an ask outstanding the pair takes a THIRD and FOURTH chip line and
 * the card reaches 160px. The pair alone is 204px of a 250px row, so it can
 * never share a line with the chips ahead of it — the cost is structural, not a
 * near-miss that a shorter label would fix. Fewer cards per screen on the one
 * surface whose whole job is "show me my lane at a glance".
 *
 * ⚠️ WHAT WE REFUSED TO LOSE A SECOND TIME. When the OLD stacked "Attempt N/3"
 * chip was removed (CTO, 2026-09-04) its danger tone went with it, and that
 * tone was the only BOARD-LEVEL warning that a threshold was about to fire:
 *   - 3 CLINICIAN attempts in a round → the callback AUTO-ESCALATES to admin
 *     (clinic-service `syncDealOnCallbackEscalated` → `awaiting_admin_followup`);
 *   - 3 ADMIN attempts → it AUTO-PARKS out of the working queue into the
 *     unreachable lane (`syncDealOnCallbackParked` → `unreachable`) and the
 *     active lead key is cleared.
 * Cards were leaving people's lanes on the third dial with nothing on the board
 * having said it was coming. That warning was restored on 2026-09-05 as the
 * danger tone of the split chips, and removing the split chips outright would
 * drop it for the SECOND time in three days.
 *
 * The answer for one day was "the warning stays on the card and only the
 * progress counting leaves" — `AttemptThresholdChip` at or past a requirement,
 * nothing on the ordinary card, 0px in the common case.
 *
 * ⚠️ CORRECTED 2026-09-06 (the four-row card, CTO): THE WARNING LEFT THE CARD
 * TOO, so there is now NO attempt chrome on the board at all. The block above
 * still stands as the reason this must never be dropped SILENTLY — it is the
 * loss that was refused twice — and the CTO accepted it here with the trade
 * named: the card is a scan surface, and the threshold is only ever ACTED on
 * inside the drawer, so the warning follows the action rather than the glance.
 *
 * WHERE BOTH WENT — and neither is deleted, deliberately:
 *   - `AttemptSummary` → the TOP of the drawer's Activity tab, beside the call
 *     rows it is a tally of.
 *   - the threshold SENTENCE → a warning `Alert` ABOVE THE TABS in
 *     `DealDrawer`, rendered verbatim from `attemptThreshold`. Above, not
 *     inside Activity: a warning you only meet after choosing the right tab is
 *     a warning that gets lost, which is precisely this chip's history.
 *   - `AttemptThresholdChip` itself is mounted by nothing but
 *     `cardAndAuditSurface.spec.tsx` today. It is KEPT, not deleted, so the
 *     surface that puts the warning back has the component and this reasoning
 *     together — but do not read its existence as evidence it renders anywhere.
 *
 * `attemptSplitChips` is unchanged and is still the ONE derivation, so the
 * chip, the banner and the summary can never disagree about the same dials.
 */

/** The owner(s) whose attempts have reached the requirement, and the sentence
 *  naming what that fires. `null` when nothing is at a threshold — which is the
 *  ordinary card, and renders no chrome whatsoever. */
export interface AttemptThreshold {
  owners: AttemptOwner[];
  /** The full sentence for title + aria-label. */
  full: string;
}

/**
 * WHAT THE THRESHOLD FIRES, per owner — the consequence, not the count.
 *
 * Stated in the operator's terms ("moves to admin", "leaves the working queue")
 * rather than the server's (`awaiting_admin_followup`, `unreachable`), because
 * the person reading it on a board is deciding whether to ring now, not
 * debugging a stage machine.
 */
const THRESHOLD_CONSEQUENCE: Record<AttemptOwner, string> = {
  clinician: 'the callback moves to admin automatically',
  admin: 'the deal leaves the working queue as unreachable',
};

/**
 * Derived from `attemptSplitChips` ON PURPOSE rather than from the card fields
 * again: one reading of the server's split feeds the board chip and the drawer
 * summary both, so the two surfaces cannot drift about whether a threshold has
 * been hit. Every "render nothing" rule that function documents (missing split,
 * missing requirement, zero counts) therefore applies here for free.
 */
export function attemptThreshold(card: DealCard): AttemptThreshold | null {
  const hit = attemptSplitChips(card).filter((chip) => chip.atThreshold || chip.exceeded);
  if (hit.length === 0) return null;
  return {
    owners: hit.map((chip) => chip.owner),
    // Both sides at their ceiling is a real state (a clinician rang out, it
    // escalated, admin rang out too). Naming both is the honest rendering —
    // picking one would hide a fired threshold behind a pending one.
    full: `Call attempt limit reached — ${hit
      .map((chip) => `${OWNER_LABEL[chip.owner]}: ${chip.count} of ${card.attemptsRequired} (${THRESHOLD_CONSEQUENCE[chip.owner]})`)
      .join('; ')}. The full tally is on the Activity tab.`,
  };
}

/**
 * THE THRESHOLD WARNING as a chip. ⚠️ MOUNTED BY NO SURFACE TODAY — it read
 * "the only attempt chrome left on the board" until the 2026-09-06 four-row
 * card took the last of that chrome off. The live warning is the `Alert` above
 * the drawer's tabs, which renders `attemptThreshold(...).full` verbatim; this
 * component is kept for whatever puts a board-level warning back, and the block
 * above `AttemptThreshold` is why one probably should.
 *
 * Icon AND words (WCAG 2.1 AA 1.4.1), never the danger colour alone: the
 * WarningIcon is decorative and the visible words "Call limit" are the
 * accessible name, with the owners and the consequence on title/aria-label. The
 * face is deliberately short — this chip exists to be noticed in a scan, and the
 * detail behind it is one click away in the drawer.
 */
export function AttemptThresholdChip({ card }: { card: DealCard }) {
  const threshold = attemptThreshold(card);
  if (!threshold) return null;
  return (
    <span
      title={threshold.full}
      aria-label={threshold.full}
      role="img"
      style={{ ...chipStyle({ bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' }), gap: '5px', marginLeft: '6px' }}
    >
      <WarningIcon size={11} />
      Call limit
    </span>
  );
}

/**
 * THE DRAWER'S ATTEMPT SUMMARY — the per-owner tally the card used to carry,
 * moved to the top of the Activity tab (CTO, 2026-09-06).
 *
 * WHY HERE and not the Overview tab, which already carries two attempt numbers
 * ("This round: N of 3 · M lifetime" and "Call attempts: N total · M this
 * round"): those two count ROUNDS and TOTALS off the callback ticket, and this
 * one counts DIALS BY OWNER off the two ledgers' `call_attempt` rows. This is
 * the summary of the very list underneath it, so it belongs above that list —
 * a reader can check the tally against the rows without changing tab.
 *
 * SAME DERIVATION AS THE BOARD CHIP, so the "Call limit" warning on the card
 * and the numbers in here are guaranteed to be describing the same dials.
 *
 * The danger tone rides the owner AT its ceiling, exactly as the old card chips
 * did, and the words "limit reached" carry it too — never colour alone.
 */
export function AttemptSummary({ card }: { card: DealCard }) {
  const chips = attemptSplitChips(card);
  if (chips.length === 0) return null;
  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: 'var(--gps-s-3)', fontSize: '12.5px' }}
      aria-label="Call attempts by owner"
      role="group"
    >
      <span style={{ color: 'var(--gps-muted)' }}>Call attempts</span>
      {chips.map((chip) => (
        <span key={chip.owner} title={chip.full} aria-label={chip.full} role="img">
          <Badge tone={chip.atThreshold || chip.exceeded ? 'danger' : 'muted'}>
            {chip.label}
            {chip.atThreshold || chip.exceeded ? ' · limit reached' : ''}
          </Badge>
        </span>
      ))}
    </div>
  );
}

/* ── Card triage marks (2026-09-05, half-shipped 2026-09-06) ────────────────
 *
 * ⚠️ ONE OF THESE IS NOW LIVE AND THE OTHER IS NOT. This block used to say BOTH
 * read fields that do not exist; that is no longer true and leaving it would
 * hide a working mark behind a comment saying it cannot work.
 *
 *   · `hasClinicalFlags` — SHIPPED. clinic-service derives it in `toCards` from
 *     `clinicalFlagsOf(Assessment.flags)`, the same vector the review screen
 *     acts on, minus the two non-clinical routing markers. The chip renders.
 *   · `untouched` — STILL ABSENT, and deliberately not approximated. It is
 *     awaiting a CTO decision on what "touched" means: the field doc says "no
 *     approve, no reject", while the rationale beside it warns a client-side
 *     proxy would wrongly mark genuinely-worked cards New — which only matters
 *     if being called or noted counts. On a triage surface the two readings
 *     send someone to a different patient first, so it stays dark until asked.
 *
 * `=== true` on both, so an older server's missing key still cannot be read as
 * a negative claim — absent renders NOTHING, never "touched", never "no flags".
 */

/** Nobody has approved or rejected this deal yet. */
export function cardIsNew(card: DealCard): boolean {
  return card.untouched === true;
}

/** The assessment behind this deal raised soft-stop / clinician-review flags. */
export function cardHasClinicalFlags(card: DealCard): boolean {
  return card.hasClinicalFlags === true;
}

const NEW_LABEL = 'New — nobody has approved or rejected this yet';

export function NewDealChip() {
  return (
    <span title={NEW_LABEL} aria-label={NEW_LABEL} role="img" style={chipStyle({ bg: 'var(--gps-info-bg)', text: 'var(--gps-info-text)' })}>
      New
    </span>
  );
}

const FLAGS_LABEL = 'This assessment raised clinical flags — open the card to read them';

/**
 * THE WARNING MARK — icon only, and it says only THAT there are flags.
 *
 * NO COUNT, deliberately, for two reasons pointing the same way. The count
 * changes no board-level decision (the card gets opened either way), and a
 * number invites triage-by-magnitude across flags that carry no weighting — two
 * mild flags would outrank one serious one. And the flag vector is
 * answer-derived special-category health data: the existence of flags is what
 * this lane needs, the flags themselves belong on the review screen where a
 * clinician acts on them.
 *
 * Icon-only follows the ReviewParkedChip idiom — the card's chip row is already
 * dense and a worded pill here would wrap it. The full wording rides title +
 * aria-label, so nothing is lost to hover or to assistive tech (WCAG 1.4.1:
 * the meaning is in the label, not in the amber).
 */
export function ClinicalFlagsChip() {
  return (
    <span
      role="img"
      aria-label={FLAGS_LABEL}
      title={FLAGS_LABEL}
      style={chipStyle({ bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' })}
    >
      <WarningIcon size={11} />
    </span>
  );
}
